import { useTranslation } from "react-i18next";
import { Clock, Hourglass, Plane, Star } from "lucide-react";
import ExpressFullIcon from "@/assets/icons/express-full.svg?react";
import type { DynamicCountsBo } from "@/types/api/api-types";
import type {
  ExpeditionsBoxFilters as FiltersState,
  useExpeditionsBoxFilters,
} from "./useExpeditionsBoxFilters.reducer";
import { FilterV2, AdditionalFilterV2, useFilterV2 } from "@/components/shared/filter-v2";
import { QuickFilterButton } from "@/components/shared/quickFilterButton";
import { SelectV2 } from "@/components/ui/customs/select-v2";
import { CustomsCombobox } from "@/components/ui/customs/customs-combobox";
import { useGetCustoms } from "@/api/customs.api";
import { useGetPickupZones } from "@/api/pickupZones.api";
import { PackagingFilterEnum } from "@/constants/enums/packagingFilter.enum";
import { COUNTRY_REGIONS, REGION_LABELS, type RegionCode } from "@/constants/countries";
import { DateRangePicker } from "@/components/ui/customs/date-range-picker";
import { usePackagingFilterOptions } from "@/hooks/useEnumOptions";

export type AssignmentMode = "all" | "mine";
export type DraftMode = "all" | "only" | "onlyMine" | "overdue48h" | "exclude";

type ExpeditionsBoxFiltersProps = {
  filters: FiltersState;
  actions: ReturnType<typeof useExpeditionsBoxFilters>["actions"];
  myUserId?: string;
  quickCounts?: DynamicCountsBo;
};

export const ExpeditionsBoxFilters = ({
  filters,
  actions,
  quickCounts,
}: ExpeditionsBoxFiltersProps) => {
  const { t } = useTranslation("boExpeditions");
  const packagingOptions = usePackagingFilterOptions();
  const reExpeditionOptions: Array<{ value: string; label: string }> = [
    { value: "true", label: t("boxFilters.reExpeditionOnly") },
    { value: "false", label: t("boxFilters.reExpeditionExcluded") },
  ];
  const { isOpen, toggle } = useFilterV2();
  const {
    customId,
    pickupZoneId,
    packagingType,
    region,
    deliveryFromDateAfter,
    deliveryFromDateBefore,
    isReExpedition,
  } = filters;

  const regionOptions = (Object.keys(COUNTRY_REGIONS) as RegionCode[]).map((code) => ({
    label: REGION_LABELS[code],
    value: code,
  }));
  const { data: customs } = useGetCustoms();
  const { data: pickupZones } = useGetPickupZones();

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex w-full flex-col items-end justify-between gap-1 xl:flex-row">
        <ExpeditionsBoxQuickFilters
          isLate={filters.isLate}
          isExpress={filters.isExpress}
          isRemote={filters.isRemote}
          isCustomQuotation={filters.isCustomQuotation}
          isDelayed={filters.isDelayed}
          counts={quickCounts}
          actions={actions}
        />
        <FilterV2
          isOpen={isOpen}
          onToggle={toggle}
          onReset={actions.resetTableFilters}
          hasAdditionalFilters
        >
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-44">
              <CustomsCombobox
                customs={customs ?? []}
                value={customId ?? undefined}
                onValueChange={actions.setCustomId}
                clearable
              />
            </div>
            <div className="w-44">
              <SelectV2
                placeholder={t("boxFilters.regionPlaceholder")}
                clearable={true}
                options={regionOptions}
                onChange={(e) => actions.setRegion((e as RegionCode) ?? null)}
                value={region ?? undefined}
                searchable={false}
              />
            </div>
          </div>
        </FilterV2>
      </div>
      <AdditionalFilterV2 isOpen={isOpen}>
        <div className="flex w-full flex-wrap items-center gap-2">
          <div className="max-w-xs min-w-[200px] flex-1">
            <SelectV2
              placeholder={t("boxFilters.pickupZonePlaceholder")}
              clearable={true}
              options={
                pickupZones?.map((zone) => ({
                  label: zone.name,
                  value: zone.id,
                })) ?? []
              }
              onChange={(e) => actions.setPickupZoneId(e)}
              value={pickupZoneId ?? undefined}
            />
          </div>
          <div className="max-w-xs min-w-[200px] flex-1">
            <SelectV2
              placeholder={t("boxFilters.packagingPlaceholder")}
              clearable={true}
              options={packagingOptions}
              onChange={(e) => actions.setPackagingType(e as PackagingFilterEnum)}
              searchable={false}
              value={packagingType ?? undefined}
            />
          </div>
          <div className="max-w-xs min-w-[200px] flex-1">
            <SelectV2
              placeholder={t("boxFilters.reExpeditionPlaceholder")}
              clearable={true}
              options={reExpeditionOptions}
              onChange={(e) =>
                actions.setIsReExpedition(e === "true" ? true : e === "false" ? false : null)
              }
              searchable={false}
              value={isReExpedition === null ? undefined : String(isReExpedition)}
            />
          </div>
          <div className="max-w-xs min-w-[200px] flex-1">
            <DateRangePicker
              placeholder={t("boxFilters.deliveryFromDatePlaceholder")}
              after={deliveryFromDateAfter}
              before={deliveryFromDateBefore}
              onChange={actions.setDeliveryFromDateRange}
            />
          </div>
        </div>
      </AdditionalFilterV2>
    </div>
  );
};

type ExpeditionsBoxQuickFiltersProps = {
  isLate: FiltersState["isLate"];
  isExpress: FiltersState["isExpress"];
  isRemote: FiltersState["isRemote"];
  isCustomQuotation: FiltersState["isCustomQuotation"];
  isDelayed: FiltersState["isDelayed"];
  counts?: DynamicCountsBo;
  actions: ReturnType<typeof useExpeditionsBoxFilters>["actions"];
};

const ExpeditionsBoxQuickFilters = ({
  isLate,
  isExpress,
  isRemote,
  isCustomQuotation,
  isDelayed,
  counts,
  actions,
}: ExpeditionsBoxQuickFiltersProps) => {
  const { t } = useTranslation("boExpeditions");
  return (
    <div className="flex flex-col gap-3 pt-2">
      <div className="flex flex-row flex-wrap gap-1">
        <QuickFilterButton
          icon={<Clock className="size-4" />}
          label={t("boxFilters.quickLate")}
          count={counts?.late}
          active={isLate}
          onClick={actions.toggleLate}
          colorClass="text-rose-500"
          activeBg="border-rose-500 bg-rose-100"
        />
        <QuickFilterButton
          icon={<ExpressFullIcon className="size-5" />}
          label={t("boxFilters.quickExpress")}
          count={counts?.express}
          active={isExpress}
          onClick={actions.toggleExpress}
          colorClass="text-[#3562f8]"
          activeBg="border-[#3562f8] bg-[#3562f8]/10"
        />
        <QuickFilterButton
          icon={<Plane className="size-4" fill="currentColor" stroke="none" />}
          label={t("boxFilters.quickRemote")}
          count={counts?.remote}
          active={isRemote}
          onClick={actions.toggleRemote}
          colorClass="text-foreground"
          activeBg="border-foreground bg-gray-100"
        />
        <QuickFilterButton
          icon={<Star className="size-4" />}
          label={t("boxFilters.quickCustomQuotation")}
          count={counts?.customQuotation}
          active={isCustomQuotation}
          onClick={actions.toggleCustomQuotation}
          colorClass="text-purple-500"
          activeBg="border-purple-500 bg-purple-100"
        />
        <QuickFilterButton
          icon={<Hourglass className="size-4" />}
          label={t("boxFilters.quickDelayed")}
          count={counts?.delayed}
          active={isDelayed}
          onClick={actions.toggleDelayed}
          colorClass="text-green-600"
          activeBg="border-green-500 bg-green-100"
        />
      </div>
    </div>
  );
};
